import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

export default class extends Controller<HTMLElement> {
  static values = {
    url: String,
    canvasListId: String,
  };

  declare readonly urlValue: string;
  declare readonly canvasListIdValue: string;

  private sortable: Sortable | null = null;

  connect() {
    this.sortable = new Sortable(this.element, {
      animation: 150,
      handle: ".section-handle",
      draggable: "[data-section-id]",
      ghostClass: "opacity-30",
      chosenClass: "shadow-lg",
      dragClass: "shadow-xl",
      forceFallback: true,
      fallbackOnBody: false,
      direction: "vertical",
      onEnd: this.onEnd.bind(this),
    });
  }

  disconnect() {
    this.sortable?.destroy();
    this.sortable = null;
  }

  private onEnd(event: Sortable.SortableEvent) {
    if (event.oldIndex === event.newIndex) return;

    const order = this.readOrderFromDom();
    this.mirrorToCanvas(order);
    this.postReorder(order);
  }

  private readOrderFromDom(): string[] {
    return Array.from(
      this.element.querySelectorAll<HTMLElement>("[data-section-id]"),
    )
      .map((el) => el.dataset.sectionId)
      .filter((id): id is string => Boolean(id));
  }

  private mirrorToCanvas(order: string[]) {
    if (!this.canvasListIdValue) return;
    const canvas = document.getElementById(this.canvasListIdValue);
    if (!canvas) return;

    order.forEach((sectionId) => {
      const node = canvas.querySelector<HTMLElement>(
        `[data-section-id="${sectionId}"]`,
      );
      if (node) canvas.appendChild(node);
    });
  }

  private async postReorder(order: string[]) {
    const positions = order.map((id, index) => ({
      id: Number(id),
      position: index,
    }));

    const csrfToken = document
      .querySelector<HTMLMetaElement>('meta[name="csrf-token"]')
      ?.getAttribute("content");

    await fetch(this.urlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": csrfToken ?? "",
        Accept: "application/json",
      },
      body: JSON.stringify({ positions }),
      credentials: "same-origin",
    });
  }
}
